"use client"

import React, {
  ChangeEvent, KeyboardEvent,
  useState, useEffect, useCallback, useRef,
  useMemo, Suspense,
} from "react"
import Link from "next/link"
import { useParams, useRouter, useSearchParams } from "next/navigation"
import toast from "react-hot-toast";
import { cn } from "@/lib/utils"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Icons } from "@/components/icons"
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  // DialogTrigger,
} from "@/components/fun-ui/Dialog";
import { Button } from "./fun-ui/Button";
import { fun } from "@/lib/request";
import { useTrace } from "@/hooks/useTrace";
import { api } from "@/lib/env";
import Loading from "@/components/Loading";
import {
  captureProductEvent,
  extractAnalyticsUserId,
  hasExplicitNewUserFlag,
  identifyProductAnalyticsUser,
} from "@/lib/analytics/product-events";

const isValidUrl = (url: string) => {
  try {
    new URL(url, window.location.origin);
    return true;
  } catch {
    return false;
  }
};

interface UserAuthFormProps extends React.HTMLAttributes<HTMLDivElement> {
  isBindMobile?: boolean;
  label?: string;
}

function UserAuthFormContent({
  className,
  isBindMobile = false,
  label = '开始使用',
  ...props
}: UserAuthFormProps) {
  const router = useRouter();
  const search = useSearchParams();
  const {
    inviteCode = '',
  } = useParams() as {
    inviteCode: string;
  };
  const callbackUrl = decodeURIComponent(search.get('callback') || '');
  const initialMobilePhone = search.get('phone') || '';
  const [isSmsLoading, setIsSMsLoading] = useState(false);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [mobile, setMobile] = useState(initialMobilePhone);
  const [smsCode, setSmsCode] = useState('');
  const [countdown, setCountdown] = useState(0); // 倒计时剩余时间
  const [readChecked, setReadChecked] = useState<boolean>(false);// 已阅读
  const readCheckedRef = useRef<HTMLLabelElement | null>(null);
  const [open, setOpen] = useState(false);

  const handleMobileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const v = e.target?.value;
    setMobile(v);
  };
  const normalizeSmsCode = (value: string) => value.replace(/\D/g, '').slice(0, 6);
  const handleSmsCodeChange = (e: ChangeEvent<HTMLInputElement>) => {
    const v = normalizeSmsCode(e.target?.value || '');
    setSmsCode(v);
  };
  const handleSmsCodeKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSubmit();
      return;
    }

    // 允许复制、粘贴、全选等系统快捷键
    if (e.ctrlKey || e.metaKey) {
      return;
    }

    const allowedKeys = [
      'Backspace',
      'Delete',
      'ArrowLeft',
      'ArrowRight',
      'Home',
      'End',
      'Tab',
    ];
    if (allowedKeys.includes(e.key)) {
      return;
    }

    // 只允许输入数字；粘贴内容会在 onChange 中清洗为 6 位数字
    if (!/^[0-9]$/.test(e.key)) {
      e.preventDefault();
    }
  };

  const { log } = useTrace();
  const handleSubmit = useCallback(async () => {
    if (!isBindMobile && !readChecked) {
      toast.error('请同意用户条款', {
        id: 'read-checked-first'
      });
      readCheckedRef.current?.classList.remove('animate-shake-horizontal')
      window.requestAnimationFrame(() => {
        readCheckedRef.current?.classList.add('animate-shake-horizontal')
      });
      return;
    }
    toast.dismiss('read-checked-first');
    if (isSubmitLoading) {
      return;
    }
    const key = isBindMobile ? 'bind' : 'login';
    try {
      setIsSubmitLoading(true);
      const formData = new FormData();
      formData.append('phone_number', mobile);
      formData.append('sms_code', smsCode);
      inviteCode && formData.append('invite_code', inviteCode);
      // 邀请页面，在PC端完成，invite_source = 100002
      inviteCode && formData.append('invite_source', '100002');
      const urls = {
        login: '/user/login',
        bind: '/register/bindPhoneNumber',
      }
      const requestUrl = urls[key];
      const { data } = await fun.post(api(requestUrl, true), formData);
      if (data.code === 0) {
        const analyticsUserId = extractAnalyticsUserId(data);
        identifyProductAnalyticsUser(analyticsUserId);

        if (key === 'login') {
          captureProductEvent("login_succeeded", { auth_method: "phone_sms" });
        }
        if (key === 'bind' || hasExplicitNewUserFlag(data)) {
          captureProductEvent("signup_completed", { auth_method: "phone_sms" });
        }

        const toUrl = inviteCode ? '/papers?from=referrer' : '/papers';
        const finalUrl = callbackUrl && isValidUrl(callbackUrl) ? callbackUrl : toUrl;
        log({
          data,
          toUrl,
          finalUrl,
        });
        router.push(finalUrl);
        return;
      } else {
        toast.error(`${data.message}`);
      }
    } catch (error) {
      const msgs = {
        login: '登录失败！请重试',
        bind: '绑定失败！请重试',
      }
      toast.error(msgs[key]);
    } finally {
      setIsSubmitLoading(false);
    }
  }, [
    readChecked,
    isSubmitLoading,
    isBindMobile,
    mobile,
    smsCode,
    inviteCode,
    router,
    callbackUrl,
    log,
  ]);


  // 验证码倒计时
  useEffect(() => {
    let timer: ReturnType<typeof setInterval> | null = null;

    if (countdown > 0) {
      // 创建定时器并设置每秒减少 countdown
      timer = setInterval(() => {
        setCountdown((prevCountdown) => prevCountdown - 1);
      }, 1000);
    } else {
      timer && clearInterval(timer);
    }

    // 组件卸载时清除定时器
    return () => {
      timer && clearInterval(timer);
    };
  }, [countdown]);

  useEffect(() => {
    // 自动提交
    if (smsCode && smsCode.length === 6 && /^1\d{10}$/.test(mobile) && readChecked) {
      handleSubmit();
    }
    // handleSubmit，readChecked 变化不触发提交
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mobile, smsCode]);


  const getVerificationCode = useCallback(async () => {
    const toastId = 'getVerificationCode';
    if (!(window as any).captchaObj) {
      return;
    }
    try {
      setIsSMsLoading(true);
      toast.loading('验证码发送中！请稍等...', {
        id: toastId
      });
      const result: {
        lot_number: string,
        captcha_output: string,
        pass_token: string,
        gen_time: string,
      } = (window as any).captchaObj.getValidate();
      const formData = {
        'phone_number': mobile,
        'scope': '6E9RrW4OLeYcJ2N0',
        'invite_code': inviteCode || '',
        ...result,
      };
      const { data }: {
        data: {
          message: string;
          code: number;
          data: Object;
        },
      } = await fun.post(api('/sms/sendAuthCode', true), formData);
      if (data.code === 0) {
        toast.success('验证码发送成功！');
        setCountdown(60);
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      toast.error('验证码请求失败！请重试');
    } finally {
      toast.dismiss(toastId);
      setIsSMsLoading(false);
    }
  }, [inviteCode, mobile]);

  const getVerificationCodeRef = useRef(getVerificationCode);
  getVerificationCodeRef.current = useMemo(() => getVerificationCode, [getVerificationCode]);

  const captchaConfig = useMemo(() => (
    {
      captchaId: "598e415426d34a668d593250c39f6ee8",
      product: "bind",
    }
  ), []);

  const handleShowCaptcha = useCallback(() => {
    if (!/^1\d{10}$/.test(mobile)) {
      toast.error('手机号格式不正确！');
      return;
    }
    if (captchaConfig.product === "bind") {
      if ((window as any).captchaObj) {
        (window as any).captchaObj.showCaptcha();
      } else {
        toast.error('请等待验证初始化完成');
        return;
      }
    } else {
      getVerificationCodeRef.current();
    }
  }, [captchaConfig.product, mobile]);

  const captchaHandler = useCallback((captchaObj: any) => {
    (window as any).captchaObj = captchaObj;
    captchaObj
      .onReady(function () {
        console.log("ready");
      })
      .onNextReady(function () {
      })
      .onBoxShow(function () {
        console.log("boxShow");
      })
      .onError(function (e: any) {
        console.log(e);
      })
      .onSuccess(() => {
        getVerificationCodeRef.current();
      });
  }, []);

  useEffect(() => {
    (window as any)?.initAlicom4?.(
      captchaConfig,
      captchaHandler,
    );
  }, [captchaConfig, captchaHandler]);

  return (
    <div className={cn(className)} {...props}>
      <form onSubmit={(e) => { e.preventDefault() }}>
        <div className="grid gap-4">
          <div className="bg-[#F4F6F7] rounded-full">
            <Label className="sr-only" htmlFor="mobile">
              手机
            </Label>
            <Input
              className="rounded-full border-none bg-transparent focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 h-[2.875rem] px-5 text-base"
              id="mobile"
              placeholder="中国大陆手机号"
              type="text"
              autoCapitalize="none"
              autoCorrect="off"
              autoComplete="none"
              value={mobile}
              onChange={handleMobileChange}
              disabled={isSmsLoading}
            />
          </div>
          <div className="">
            <div className="flex justify-between bg-[#F4F6F7] rounded-full">
              <Input
                className="rounded-full border-none bg-transparent focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 h-[2.875rem] pl-5 text-base flex-1 w-3/5"
                id="smsCode"
                placeholder="验证码"
                type="text"
                autoCapitalize="none"
                autoCorrect="off"
                autoComplete="one-time-code"
                inputMode="numeric"
                pattern="[0-9]*"
                value={smsCode}
                onChange={handleSmsCodeChange}
                disabled={isSmsLoading}
                maxLength={6}
                onKeyDown={handleSmsCodeKeyDown}
              />
              <Button
                className="h-[2.875rem] min-w-[6.875rem] text-base no-underline hover:no-underline active:no-underline pl-0 pr-5 justify-end"
                variant="link"
                onClick={handleShowCaptcha}
                disabled={isSmsLoading || countdown > 0}
              >
                {isSmsLoading ? '获取中...' : countdown > 0 ? `${countdown}秒后获取` : '获取验证码'}
              </Button>
            </div>
            {
              (isBindMobile) ? null :
                <div className="text-annotation-foreground text-sm mt-3">
                  未注册的手机号验证通过后将自动注册
                </div>
            }
          </div>
          <Button
            className="rounded-full h-[2.875rem] text-base"
            disabled={isSubmitLoading || !(mobile && smsCode)}
            onClick={() => {
              if (isBindMobile) {
                handleSubmit();
              } else if (readChecked) {
                handleSubmit();
              } else {
                setOpen(true);
                setReadChecked(true);
              }
            }}
          >
            {isSubmitLoading && (
              <Icons.spinner className="mr-2 h-4 w-4 animate-spin" />
            )}
            {label}
          </Button>
          {
            (isBindMobile) ? null : (
              <label ref={readCheckedRef} className="text-sm flex w-full min-w-0 items-center space-x-1 mt-2 px-1">
                <Checkbox checked={readChecked} onCheckedChange={(checked) => {
                  setReadChecked(!!checked);
                }} />
                <span className="min-w-0 leading-5">我已阅读并同意《<Link className="text-primary hover:underline" href="/docs/software-license-and-services-agreement">用户协议</Link>》和《<Link className="text-primary hover:underline" href="/docs/privacy-protection-agreement">隐私政策</Link>》</span>
              </label>
            )
          }
        </div>
      </form >
      {
        (isBindMobile) ? null : (
          <UserAgreementDialog
            open={open}
            setOpen={setOpen}
            onCancel={() => {
              setReadChecked(false);
              setOpen(false);
            }}
            onConfirm={async () => {
              await handleSubmit();
            }}
          />
        )
      }
    </div >
  )
}

export function UserAuthForm({
  className,
  isBindMobile = false,
  label = '开始使用',
  ...props
}: UserAuthFormProps) {
  return (
    <Suspense fallback={<Loading />}>
      <UserAuthFormContent
        className={className}
        isBindMobile={isBindMobile}
        label={label}
        {...props}
      />
    </Suspense>
  );
}

export function UserAgreementDialog({
  open,
  setOpen,
  onCancel,
  onConfirm,
}: {
  open: boolean,
  setOpen: (open: boolean) => void,
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="w-[400px]">
        <DialogHeader>
          <DialogTitle className="text-base">协议确认</DialogTitle>
        </DialogHeader>
        <div className="text-sm">
          <span>您需要阅读并同意 Findin《<Link className="text-primary hover:underline" href="/docs/software-license-and-services-agreement">用户协议</Link>》和《<Link className="text-primary hover:underline" href="/docs/privacy-protection-agreement">隐私政策</Link>》<br />我们才能为你提供服务</span>
        </div>
        <DialogFooter className="gap-4">
          <Button
            variant="outline"
            onClick={onCancel}
          >
            取消
          </Button>
          <Button
            className="min-w-[8.125rem]"
            onClick={onConfirm}
          >
            同意并登录
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
